import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateParserFormatter, NgbDateStruct, NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import {
  DATE_RANGE_FILTER_KEY,
  SharedCacheService,
} from 'src/app/nio-common/services/shared-cache.service';
import { DateRange, DateRangeProperty } from '../../models/date-range.model';
import { CustomDateParserService } from '../../services/custom-date-parser.service';

const DATE_FORMAT = 'DD.MM.YYYY';

@Component({
  selector: 'app-date-range',
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.scss'],
  providers: [
    { provide: 'dateFormat', useValue: DATE_FORMAT },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserService },
  ],
})
export class DateRangeComponent implements OnInit, OnDestroy {
  @Input() set dateRangeCont(range: DateRangeProperty) {
    if (!range || !range.from || !range.to) return;
    this.dateRange = range;

    const a = moment(range.from);
    const b = moment(range.to);

    this.onPreviousRangeClick({
      to: range.to,
      from: range.from,
      toDate: b.format(DATE_FORMAT),
      fromDate: a.format(DATE_FORMAT),
    });

    this.updateDateRangeText();
  }
  @Input() dateRange: DateRangeProperty = {};
  @Input() selectedAsDefault = true;
  @Input() defaultRange!: DateRange;
  @Output() rangeSelect = new EventEmitter<DateRangeProperty>();

  @ViewChild('dateRangeDrop')
  dropdown?: NgbDropdown;
  form!: FormGroup;
  dateRangeProperty: DateRangeProperty = {};
  internalDateRange?: DateRange | null;
  previousRanges: DateRangeProperty[] = [];
  dateRangeText!: string;
  maxDateStart?: NgbDateStruct;
  minDateEnd?: NgbDateStruct;
  maxDateEnd?: NgbDateStruct;

  private destroyed$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly dateParser: NgbDateParserFormatter,
    private readonly cacheService: SharedCacheService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.form = this.fb.group({
      startDate: [null, Validators.required],
      endDate: [null, Validators.required],
    });

    this.previousRanges = this.getRangesFromLocalStorage();

    await this.updateRange();
    this.form.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val: { startDate: NgbDateStruct; endDate: NgbDateStruct }) => {
        this.maxDateStart = val.endDate;
        this.minDateEnd = val.startDate;
      });
    this.patchFormDataByRange();
    this.emitAndClose();

    const today = moment();
    this.maxDateEnd = { year: today.year() + 5, month: today.month() + 1, day: today.date() };
  }

  private async updateRange() {
    let to = moment();
    let from = moment();

    if (!this.dateRange?.from || !this.dateRange?.to) {
      if (this.selectedAsDefault && this.cacheService.has(DATE_RANGE_FILTER_KEY)) {
        const selectedRange: DateRangeProperty = await lastValueFrom(
          this.cacheService.get(DATE_RANGE_FILTER_KEY),
        );
        if (!selectedRange?.dateRange && selectedRange?.to && selectedRange?.from) {
          to = moment(selectedRange.to);
          from = moment(selectedRange.from);
          this.internalDateRange = null;
        } else if (selectedRange?.dateRange) {
          this.internalDateRange = selectedRange.dateRange;
        }
      } else {
        this.internalDateRange = this.defaultRange;
      }
    }

    if (!this.internalDateRange) {
      this.dateRangeProperty.from = (
        this.dateRange?.from ? moment(this.dateRange.from) : from
      ).toISOString();
      this.dateRangeProperty.to = (
        this.dateRange?.to ? moment(this.dateRange.to) : to
      ).toISOString();
    }
  }

  patchFormDataByRange(patchFormData = true) {
    if (this.form) {
      switch (this.internalDateRange) {
        case DateRange.THIS_YEAR:
          this.dateRangeProperty.to = moment().endOf('year').toISOString();
          this.dateRangeProperty.from = moment(this.dateRangeProperty.to)
            .startOf('year')
            .toISOString();
          break;
        case DateRange.LAST_WEEK:
          this.dateRangeProperty.to = moment().endOf('day').toISOString();
          this.dateRangeProperty.from = moment(this.dateRangeProperty.to)
            .subtract(7, 'days')
            .startOf('day')
            .toISOString();
          break;
        case DateRange.LAST_2_MONTH:
          this.dateRangeProperty.to = moment().endOf('day').toISOString();
          this.dateRangeProperty.from = moment(this.dateRangeProperty.to)
            .subtract(2, 'months')
            .startOf('day')
            .toISOString();
          break;
        case DateRange.LAST_YEAR:
          this.dateRangeProperty.to = moment().endOf('day').toISOString();
          this.dateRangeProperty.from = moment(this.dateRangeProperty.to)
            .subtract(1, 'years')
            .startOf('day')
            .toISOString();
          break;
        default:
          if (this.form.valid) {
            const value = this.form.value;
            this.dateRangeProperty.to = moment(
              `${this.dateParser.format(value.endDate)}`,
              `${DATE_FORMAT}`,
            )
              .endOf('day')
              .toISOString();
            this.dateRangeProperty.from = moment(
              `${this.dateParser.format(value.startDate)}`,
              `${DATE_FORMAT}`,
            )
              .startOf('day')
              .toISOString();
          }
          break;
      }
      if (patchFormData) {
        this.form
          .get('startDate')
          ?.setValue(
            this.dateParser.parse(moment(this.dateRangeProperty.from).format(DATE_FORMAT)),
          );
        this.form
          .get('endDate')
          ?.setValue(this.dateParser.parse(moment(this.dateRangeProperty.to).format(DATE_FORMAT)));
      }
    }
    this.updateDateRangeText();
  }

  private updateDateRangeText() {
    this.dateRangeText = `
      ${moment(this.dateRangeProperty.from).format(DATE_FORMAT) || ''} -
      ${moment(this.dateRangeProperty.to).format(DATE_FORMAT) || ''}
    `;
  }

  private updatePreviousRanges() {
    const prev = this.previousRanges.find(
      range => range.to === this.dateRangeProperty.to && range.from === this.dateRangeProperty.from,
    );

    const range = this.dateRangeProperty;
    range.toDate = moment(range.to).format(DATE_FORMAT);
    range.fromDate = moment(range.from).format(DATE_FORMAT);

    if (!prev) {
      this.previousRanges.unshift(range);
      if (this.previousRanges.length > 2) this.previousRanges.pop();
    }

    this.setPreviousRanges(this.previousRanges);
  }

  public get dateRangeEnum(): typeof DateRange {
    return DateRange;
  }

  private getRangesFromLocalStorage(): DateRangeProperty[] {
    const ranges = JSON.parse(
      window.localStorage.getItem('previousRanges') || '[]',
    ) as DateRangeProperty[];
    return ranges.map(range => {
      range.toDate = moment(range.to).format(DATE_FORMAT);
      range.fromDate = moment(range.from).format(DATE_FORMAT);
      return range;
    });
  }

  private setPreviousRanges(ranges: DateRangeProperty[]) {
    this.previousRanges = ranges;
    const mapped = ranges.map(range => {
      return { to: range.to, from: range.from };
    });
    window.localStorage.setItem('previousRanges', JSON.stringify(mapped));
  }

  emitAndClose() {
    if (this.dateRangeProperty?.to) {
      this.updateSelectedRange();
      this.rangeSelect.emit(this.dateRangeProperty);
      if (this.dropdown) {
        this.dropdown.close();
      }
    }
  }

  updateSelectedRange() {
    this.cacheService.set(DATE_RANGE_FILTER_KEY, {
      dateRange: this.internalDateRange,
      to: !this.internalDateRange ? this.dateRangeProperty.to : null,
      from: !this.internalDateRange ? this.dateRangeProperty.from : null,
    } as DateRangeProperty);
  }

  onRange(range: DateRange) {
    this.internalDateRange = range;
    this.patchFormDataByRange();
    this.emitAndClose();
  }

  onSubmit() {
    if (this.form.valid) {
      this.internalDateRange = null;
      this.patchFormDataByRange(false);
      this.emitAndClose();
      this.updatePreviousRanges();
    }
  }

  onReset() {
    this.onRange(this.defaultRange);
  }

  onPreviousRangeClick(range: DateRangeProperty) {
    if (!this.form) {
      return;
    }

    this.internalDateRange = null;
    this.form.patchValue({
      endDate: this.dateParser.parse(range.toDate || ''),
      startDate: this.dateParser.parse(range.fromDate || ''),
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
